<template>
  <Layout>
    <div class="mc-list-header">
      <PageHeader class="mc-page-three" :title="title" hasBreadcrumb :items="items" />
      <p class="mb-4">
        {{ $t('dashboard.deliverSummary.desc') }}
      </p>
      <div class="clearfix mc-btn-group__list mc-btn-group__list--detail">
        <McButtonBack class="d-inline-block me-2" href="/dashboard/" />
        <McButton
          v-b-modal.history-email
          variant="secondary"
          icon="icon-outline_file-text"
          :text="$t('dashboard.scheduler.history')"
        />
      </div>
    </div>
    <McGrapqlTable
      v-if="!refetchTable"
      ref="dashboardSchedulerRef"
      :apiClient="query"
      :fields="fields"
      :customHandlers="customHandlers"
      :hasCheckbox="true"
      :searchable="true"
      :isSelectableField="false"
      :withCard="true"
      :stickyHeader="false"
      trackBy="reportSchedulerId"
      class="mc-table--detail"
    >
      <template #thead-top>
        <b-tr>
          <b-th><span class="checkbox"></span></b-th>
          <b-th rowspan="2">
            <div class="d-flex align-items-center">
              {{ $t('report.title.scheduleName') }}
            </div>
          </b-th>
          <b-th rowspan="2">
            <div class="d-flex align-items-center">
              {{ 'Status' }}
            </div>
          </b-th>
          <b-th colspan="2" class="text-center">
            {{ $t('report.object.reportRecipient') }}
          </b-th>
          <b-th rowspan="2">
            <div class="d-flex align-items-center">
              {{ $t('dashboard.scheduler.startDate') }}
            </div>
          </b-th>
          <b-th rowspan="2">
            <div class="d-flex align-items-center">
              {{ $t('dashboard.scheduler.sendPeriod') }}
            </div>
          </b-th>
          <b-th rowspan="2" />
        </b-tr>
      </template>
      <template #cell(detail)="data">
        <b-dropdown size="lg" variant="link" gpp wtoggle-class="text-decoration-none" no-caret>
          <template #button-content>
            <i class="mc-fa icon-fill_more-vertical"></i>
            <span class="sr-only">{{ $t('dashboard.scheduler.action.detail') }}</span>
          </template>
          <b-dropdown-item @click="handleSentNow(data.item.reportSchedulerId)">
            {{ $t('dashboard.scheduler.action.sentNow') }}
          </b-dropdown-item>
          <b-dropdown-item @click="openPreview(data.item)">
            {{ $t('dashboard.scheduler.action.preview') }}
          </b-dropdown-item>
          <b-dropdown-item
            @click="
              showModalStatus(
                data.item.reportSchedulerId,
                data.item.isActive,
                data.item.scheduleName
              )
            "
          >
            <span v-if="data.item.isActive">{{ $t('global.nonActive') }}</span>
            <span v-else>{{ $t('global.active') }}</span>
          </b-dropdown-item>
          <b-dropdown-item
            @click="showModalDelete(data.item.reportSchedulerId, data.item.scheduleName)"
          >
            <span class="red-500">{{ $t('global.action.delete') }}</span>
          </b-dropdown-item>
        </b-dropdown>
      </template>
      <template #cell(whatsapp)="data">
        <div class="text-wrap">
          {{ data.item.aggregate.whatsapp }}
        </div>
      </template>
      <template #cell(recipients)="data">
        <div class="text-wrap">
          {{ data.item.aggregate.email }}
        </div>
      </template>
      <template #cell(isActive)="data">
        <McBadge
          v-if="data.item.isActive"
          :text="$t('global.active')"
          variant="success"
          type="status"
        />
        <McBadge v-else :text="$t('global.nonActive')" variant="danger" type="status" />
      </template>
      <template #cell(startTime)="data">
        <span>{{ data.item.startTime ? momentDateLongMonth(data.item.startTime) : '-' }}</span>
      </template>
      <template #cell(periodLabel)="data">
        <span>{{ data.item.periodLabel ? data.item.periodLabel : '-' }}</span>
      </template>
    </McGrapqlTable>
    <EmailHistory />
    <ModalPreviewEmail
      ref="modalPreviewEmailRef"
      :emailBody="preview.content"
      :emailLayout="preview.widgetId"
    />

    <ModalPreviewEmailDaily
      ref="modalPreviewEmailDailyRef"
      :emailBody="preview.content"
      :emailLayout="preview.widgetId"
    />

    <ModalPreviewEmailWeekly
      ref="modalPreviewEmailWeeklyRef"
      :emailBody="preview.content"
      :emailLayout="preview.widgetId"
    />
    <b-modal
      id="delete-scheduler"
      size="md"
      :title="$t('dashboard.scheduler.deleteScheduler')"
      centered
    >
      <p>
        {{ $t('dashboard.scheduler.modal.delete') }}
        <span class="fw-bold">{{ scheduleName }}</span> ?
      </p>
      <template #modal-footer="{ cancel }">
        <div class="d-flex">
          <McButton
            variant="secondary"
            class="me-2"
            :text="$t('global.cancel')"
            @click="cancel()"
          />
          <McButton variant="primary" :text="$t('global.delete')" @click="handleDelete" />
        </div>
      </template>
    </b-modal>

    <b-modal
      id="switch-status"
      size="md"
      :title="$t('dashboard.scheduler.schedulerStatus')"
      centered
    >
      <p>
        {{ $t('dashboard.scheduler.modal.changeStatus') }}
        <span class="fw-bold">{{ scheduleName }}</span>
        {{
          isActive
            ? $t('dashboard.scheduler.modal.nonActive')
            : $t('dashboard.scheduler.modal.active')
        }}
        ?
      </p>
      <template #modal-footer="{ cancel }">
        <div class="d-flex">
          <McButton
            variant="secondary"
            class="me-2"
            :text="$t('global.cancel')"
            @click="cancel()"
          />
          <McButton variant="primary" :text="$t('global.yes')" @click="handleStatusChange" />
        </div>
      </template>
    </b-modal>
  </Layout>
</template>
<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header-v2'
import Layout from '@/router/layouts/main'
import McGrapqlTable from '@/components/McGrapqlTable'
import McBadge from '@/components/McBadge'
import McButton from '@/components/McButton'
import EmailHistory from './Modal/emailHistory'
import ModalPreviewEmail from './Modal/ModalPreviewEmail'
import ModalPreviewEmailDaily from './Modal/ModalPreviewEmailDaily'
import ModalPreviewEmailWeekly from './Modal/ModalPreviewEmailWeekly'
import McButtonBack from '@/components/McButtonBack'
import { Helper } from '@/mixin/helper'
import {
  GET_SCHEDULERS,
  DELETE_SCHEDULER,
  SEND_REPORT_HTML,
} from '@/services/api/dashboard/scheduler'
import { getSummaryDigest } from '@/services/rest-api-identity/lib/metabase/custom'
import { UPDATE_REPORT_SCHEDULER } from '@/services/api/report/report'

export default {
  page: {
    title: 'Kirim Rangkuman',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McGrapqlTable,
    McButtonBack,
    EmailHistory,
    ModalPreviewEmail,
    ModalPreviewEmailDaily,
    ModalPreviewEmailWeekly,
    McBadge,
    McButton,
  },
  mixins: [Helper],
  data() {
    return {
      data: {},
      preview: {},
      customHandlers: {
        handleAdd: () => {
          return this.$router.push({ name: 'Create Dashboard Scheduler' })
        },
        handleClickRow: (item) => {
          return this.$router.push({
            name: 'Update Dashboard Scheduler',
            params: { id: item.reportSchedulerId },
          })
        },
      },
      refetchTable: false,
      reportSchedulerId: 0,
      isActive: null,
      scheduleName: null,
    }
  },
  computed: {
    title() {
      return this.$t('dashboard.deliverSummary.title')
    },

    items() {
      return [
        {
          text: this.$t('help.books.dasbor'),
          to: '/',
        },
        {
          text: this.$t('dashboard.deliverSummary.title'),
        },
      ]
    },
    fields() {
      return [
        {
          key: 'scheduleName',
          label: this.$t('report.title.scheduleName'),
          thClass: 'mc-table--250 d-none',
          isRedirectable: true,
        },
        {
          key: 'isActive',
          label: 'Status',
          thClass: 'mc-table--100 d-none',
        },
        {
          key: 'whatsapp',
          label: 'WhatsApp',
          thClass: 'mc-table--100',
        },
        {
          key: 'recipients',
          label: this.$t('notification.setting.emailNotification.email'),
          thClass: 'mc-table--100',
        },
        {
          key: 'startTime',
          label: this.$t('dashboard.scheduler.startDate'),
          thClass: 'd-none',
        },
        {
          key: 'periodLabel',
          label: this.$t('dashboard.scheduler.sendPeriod'),
          thClass: 'd-none',
        },
        {
          key: 'detail',
          label: '',
          thClass: 'd-none',
        },
      ]
    },

    query() {
      return {
        get: async (filter) => {
          const res = await this.$apollo.query({
            query: GET_SCHEDULERS,
            fetchPolicy: 'no-cache',
            variables: {
              filter: {
                ...filter,
                reportKey: 'emailDigest',
                orderBy: JSON.stringify({ reportSchedulerId: 'desc' }),
              },
            },
          })

          return res.data.reportSchedulers.reportSchedulers
        },

        delete: async (id) => {
          const res = await this.$apollo.query({
            query: DELETE_SCHEDULER,
            fetchPolicy: 'no-cache',
            variables: {
              input: {
                reportSchedulerIds: [Number(id)],
              },
            },
          })

          return res.data.deleteReportScheduler.isSuccessful
        },
      }
    },
  },
  methods: {
    getContentList(val) {
      switch (val) {
        case 'd':
          return 'daily'
        case 'w':
          return 'weekly'
        case 'm':
          return 'monthly'
      }
    },
    async getSummaryDigest(value) {
      const category = this.getContentList(value.period)
      const res = await getSummaryDigest(category)
      if (res?.data.length > 0 && value.reportSchedulerContent.length > 0) {
        return res.data.filter(
          (item) => item.content_id == value.reportSchedulerContent[0].contentId
        )
      } else return []
    },
    async openPreview(widget) {
      const res = await this.getSummaryDigest(widget)
      const content = widget.body
      const [hours, minutes, seconds] = widget.time.split(':').map(Number)

      const dayOptions = [
        { value: 0, text: this.$t('global.days.sunday') },
        { value: 1, text: this.$t('global.days.monday') },
        { value: 2, text: this.$t('global.days.tuesday') },
        { value: 3, text: this.$t('global.days.wednesday') },
        { value: 4, text: this.$t('global.days.thursday') },
        { value: 5, text: this.$t('global.days.friday') },
        { value: 6, text: this.$t('global.days.saturday') },
      ]
      const day = dayOptions.find((day) => day.value == widget.noOfDay)
      const timeObject = new Date()
      timeObject.setHours(hours)
      timeObject.setMinutes(minutes)
      timeObject.setSeconds(seconds)

      let alert = null
      if (widget.period == 'd') {
        alert = this.$t('dashboard.scheduler.preview.dashboardDayInfo', {
          time: this.momentTime(timeObject),
          startTime: this.momentDateLongMonth(widget.startTime),
        })
      } else if (widget.period == 'w') {
        alert = this.$t('dashboard.scheduler.preview.dashboardWeekInfo', {
          day: day.text,
          time: this.momentTime(timeObject),
          startTime: this.momentDateLongMonth(widget.startTime),
        })
      } else {
        alert = this.$t('dashboard.scheduler.preview.dashboardMonthInfo', {
          date: widget.noOfDay,
          time: this.momentTime(timeObject),
          startTime: this.momentDateLongMonth(widget.startTime),
        })
      }
      this.$refs.modalPreviewEmailRef.showModal(content, alert, widget, res)
    },
    convertLanguage(item) {
      if (item == 'summary') {
        return this.$t('dashboard.scheduler.widget.summary')
      }
      if (item == 'tripsStatistic') {
        return this.$t('dashboard.scheduler.widget.tripStatistic')
      }
      if (item == 'tripMileage') {
        return this.$t('dashboard.scheduler.widget.tripMileage')
      }
      if (item == 'avgSpeed') {
        return this.$t('dashboard.scheduler.widget.avgSpeed')
      }
      if (item == 'engineOnDuration') {
        return this.$t('dashboard.scheduler.widget.engineOnDuration')
      }
      if (item == 'idleDuration') {
        return this.$t('dashboard.scheduler.widget.idleDuration')
      }
      if (item == 'driverViolation') {
        return this.$t('dashboard.scheduler.widget.driverViolation')
      }
      if (item == 'totalTrip') {
        return this.$t('dashboard.scheduler.widget.totalTrip')
      } else {
        return item
      }
    },
    async handleSentNow(id) {
      const res = await this.$apollo.mutate({
        mutation: SEND_REPORT_HTML,
        variables: {
          input: {
            reportSchedulerId: Number(id),
          },
        },
      })
      if (res.data.sendReportHtml.isSuccessful) {
        this.$mcToast.success(this.$t('dashboard.scheduler.dashboardSent'))
      } else {
        this.$mcToast.error(res.data.sendReportHtml.message)
      }
    },
    async handleDelete() {
      this.refetchTable = true

      const res = await this.$apollo.mutate({
        mutation: DELETE_SCHEDULER,
        variables: {
          input: {
            reportSchedulerIds: [Number(this.reportSchedulerId)],
          },
        },
      })
      this.$bvModal.hide('delete-scheduler')
      this.refetchTable = false
      if (res.data.deleteReportScheduler.isSuccessful) {
        this.$mcToast.success(this.$t('global.alert.text.delete'))
      } else {
        this.$mcToast.error(res.data.deleteReportScheduler.message)
      }
    },

    handleStatusChange() {
      this.refetchTable = true
      const input = {
        id: this.reportSchedulerId,
        isActive: !this.isActive,
      }

      this.$apollo
        .mutate({
          mutation: UPDATE_REPORT_SCHEDULER,
          variables: {
            input: input,
          },
        })
        .then((response) => {
          this.$bvModal.hide('switch-status')
          this.refetchTable = false
          if (response.data.updateReportScheduler.isSuccessful) {
            this.$mcToast.success(this.$t('global.alert.text.changed'))
          } else {
            return this.$mcToast.error(response.data.updateReportScheduler.message)
          }
        })
    },

    showModalDelete(id, name) {
      this.reportSchedulerId = id
      this.scheduleName = name
      this.$bvModal.show('delete-scheduler')
    },

    showModalStatus(id, status, name) {
      this.reportSchedulerId = id
      this.isActive = status
      this.scheduleName = name
      this.$bvModal.show('switch-status')
    },

    cancelDelete() {
      this.$bvModal.hide('delete-scheduler')
    },

    cancelStatus() {
      this.$bvModal.hide('switch-status')
    },
  },
}
</script>
<style lang="scss" scoped>
.checkbox {
  &:before {
    content: '';
    width: 100%;
    background-color: #fafcff;
    height: 4px;
    position: absolute;
    left: 0px;
    top: 39px;
  }
}
</style>
